import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 */
export default class ResultsList extends PaginatedList {
  #qomoorVisibleCount = 0;
  #qomoorLoading = false;
  #qomoorFilterObserver = null;
  #qomoorFilterTimeout = null;
  #qomoorResizeTimeout = null;
  #qomoorLastBatchSize = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#isQomoorCollection()) {
      this.#ensureQomoorProductStyles();
      this.addEventListener('click', this.#handleQomoorClick);
      document.addEventListener(ThemeEvents.FilterUpdate, this.#handleQomoorFilterUpdate);
      window.addEventListener('resize', this.#handleQomoorResize);
      requestAnimationFrame(() => this.#resetQomoorProducts());
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#isQomoorCollection()) {
      this.removeEventListener('click', this.#handleQomoorClick);
      document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleQomoorFilterUpdate);
      window.removeEventListener('resize', this.#handleQomoorResize);
      this.#qomoorFilterObserver?.disconnect();
      clearTimeout(this.#qomoorFilterTimeout);
      clearTimeout(this.#qomoorResizeTimeout);
    }

    super.disconnectedCallback();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #isQomoorCollection() {
    return this.classList.contains('qomoor-collection-page');
  }

  #qomoorBatchSize() {
    if (
      this.classList.contains('qomoor-costume-products')
      && window.matchMedia('(min-width: 990px)').matches
    ) return 12;
    if (window.matchMedia('(min-width: 990px)').matches) return 8;
    if (window.matchMedia('(min-width: 750px)').matches) return 6;
    return 4;
  }

  #qomoorGrid() {
    return this.querySelector('[ref="grid"]');
  }

  #qomoorCards() {
    const grid = this.#qomoorGrid();
    if (!grid) return [];
    return [...grid.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  #qomoorNextLink() {
    return this.querySelector('.pagination .pagination__item:last-child a.pagination__link--arrow');
  }

  #ensureQomoorProductStyles() {
    if (document.getElementById('qomoor-product-view-more-styles')) return;

    const style = document.createElement('style');
    style.id = 'qomoor-product-view-more-styles';
    style.textContent = `
      .qomoor-collection-page .pagination {
        display: none !important;
      }

      .qomoor-collection-page .product-grid__item[hidden] {
        display: none !important;
      }

      .qomoor-collection-page .qomoor-product-view-more-wrap {
        display: flex;
        justify-content: center;
        width: 100%;
        padding: 34px 0 10px;
      }

      .qomoor-collection-page .qomoor-product-view-more-wrap[hidden] {
        display: none !important;
      }

      .qomoor-collection-page .qomoor-product-view-more {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        min-width: 188px;
        min-height: 46px;
        padding: 13px 26px;
        border: 1px solid #171717;
        background: #171717;
        color: #ffffff;
        font: inherit;
        font-size: .7rem;
        font-weight: 700;
        line-height: 1;
        letter-spacing: .11em;
        text-transform: uppercase;
        cursor: pointer;
        transition: opacity .18s ease, transform .18s ease;
      }

      .qomoor-collection-page .qomoor-product-view-more:hover {
        opacity: .84;
      }

      .qomoor-collection-page .qomoor-product-view-more:active {
        transform: translateY(1px);
      }

      .qomoor-collection-page .qomoor-product-view-more:disabled {
        opacity: .55;
        cursor: wait;
      }

      @media screen and (min-width: 990px) {
        .qomoor-collection-page .product-grid {
          --product-grid-columns-desktop: repeat(4, minmax(0, 1fr)) !important;
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        }
      }

      @media screen and (min-width: 750px) and (max-width: 989px) {
        .qomoor-collection-page .product-grid {
          --product-grid-columns-desktop: repeat(3, minmax(0, 1fr)) !important;
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }
      }

      @media screen and (max-width: 749px) {
        .qomoor-collection-page .product-grid {
          --mobile-columns: 2 !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }

        .qomoor-collection-page .qomoor-product-view-more-wrap {
          padding-top: 26px;
        }

        .qomoor-collection-page .qomoor-product-view-more {
          width: min(100%, 260px);
        }
      }
    `;
    document.head.append(style);
  }

  #ensureQomoorButton() {
    const grid = this.#qomoorGrid();
    if (!grid) return null;

    let wrap = this.querySelector('.qomoor-product-view-more-wrap');
    let button = wrap?.querySelector('.qomoor-product-view-more');

    if (!wrap || !button) {
      wrap = document.createElement('div');
      wrap.className = 'qomoor-product-view-more-wrap';

      if (this.classList.contains('qomoor-party-products--sale')) {
        button = document.createElement('a');
        button.href = '/collections/sale-featured';
        button.className = 'qomoor-product-view-more';
        button.textContent = 'VIEW MORE';
        button.setAttribute('aria-label', 'View Featured Sale');
      } else {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'qomoor-product-view-more';
        button.textContent = 'VIEW MORE';
        button.setAttribute('aria-controls', 'ResultsList');
      }

      wrap.append(button);

      const pagination = this.querySelector('.pagination');
      if (pagination) {
        pagination.insertAdjacentElement('afterend', wrap);
      } else {
        grid.insertAdjacentElement('afterend', wrap);
      }
    }

    return { wrap, button };
  }

  #resetQomoorProducts() {
    const cards = this.#qomoorCards();
    if (!cards.length) return;

    this.#qomoorLastBatchSize = this.#qomoorBatchSize();
    this.#qomoorVisibleCount = Math.min(this.#qomoorLastBatchSize, cards.length);
    this.#applyQomoorVisibility();
  }

  #applyQomoorVisibility() {
    const cards = this.#qomoorCards();

    cards.forEach((card, index) => {
      card.hidden = index >= this.#qomoorVisibleCount;
    });

    const controls = this.#ensureQomoorButton();
    if (!controls) return;

    const hasHiddenLoadedCards = this.#qomoorVisibleCount < cards.length;
    const hasNextPage = Boolean(this.#qomoorNextLink());
    controls.wrap.hidden = !(hasHiddenLoadedCards || hasNextPage);
  }

  async #loadQomoorNextPage() {
    const nextLink = this.#qomoorNextLink();
    const grid = this.#qomoorGrid();
    if (!nextLink || !grid) return false;

    const nextUrl = new URL(nextLink.href, window.location.origin);
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextUrl);
    const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
    const parsedGrid = parsedPage.querySelector('[ref="grid"]');
    const incomingCards = parsedGrid?.querySelectorAll(':scope > [ref="cards[]"]');

    if (!parsedGrid || !incomingCards?.length) return false;

    grid.dataset.lastPage = parsedGrid.dataset.lastPage || grid.dataset.lastPage;
    grid.append(...incomingCards);

    const currentPagination = this.querySelector('.pagination');
    const nextPagination = parsedPage.querySelector('.pagination');

    if (currentPagination && nextPagination) {
      currentPagination.replaceWith(nextPagination);
    } else if (currentPagination && !nextPagination) {
      currentPagination.remove();
    } else if (!currentPagination && nextPagination) {
      grid.insertAdjacentElement('afterend', nextPagination);
    }

    return true;
  }

  #handleQomoorClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.qomoor-product-view-more');
    if (!button || this.#qomoorLoading) return;
    if (button instanceof HTMLAnchorElement) {
      event.preventDefault();
      window.location.assign(button.href);
      return;
    }

    const batchSize = this.#qomoorBatchSize();
    const targetVisibleCount = this.#qomoorVisibleCount + batchSize;

    this.#qomoorLoading = true;
    button.disabled = true;
    button.textContent = 'LOADING…';

    try {
      while (this.#qomoorCards().length < targetVisibleCount && this.#qomoorNextLink()) {
        const loaded = await this.#loadQomoorNextPage();
        if (!loaded) break;
      }

      this.#qomoorVisibleCount = Math.min(targetVisibleCount, this.#qomoorCards().length);
      this.#applyQomoorVisibility();
    } catch (error) {
      console.error('Unable to load more Qomoor collection products.', error);
    } finally {
      this.#qomoorLoading = false;
      button.disabled = false;
      button.textContent = 'VIEW MORE';
      this.#applyQomoorVisibility();
    }
  };

  #handleQomoorFilterUpdate = () => {
    if (!this.#isQomoorCollection()) return;

    this.#qomoorFilterObserver?.disconnect();
    clearTimeout(this.#qomoorFilterTimeout);

    const beforeSignature = this.#qomoorSignature();

    const resetWhenUpdated = () => {
      if (!this.isConnected) return;
      this.#qomoorVisibleCount = 0;
      requestAnimationFrame(() => this.#resetQomoorProducts());
    };

    this.#qomoorFilterObserver = new MutationObserver(() => {
      if (this.#qomoorSignature() === beforeSignature) return;
      this.#qomoorFilterObserver?.disconnect();
      resetWhenUpdated();
    });

    this.#qomoorFilterObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page', 'data-product-id'],
    });

    this.#qomoorFilterTimeout = setTimeout(() => {
      this.#qomoorFilterObserver?.disconnect();
      resetWhenUpdated();
    }, 1600);
  };

  #qomoorSignature() {
    const grid = this.#qomoorGrid();
    const ids = this.#qomoorCards().slice(0, 3).map((card) => card.dataset.productId || '').join(',');
    return `${grid?.dataset.lastPage || ''}|${this.#qomoorCards().length}|${ids}`;
  }

  #handleQomoorResize = () => {
    clearTimeout(this.#qomoorResizeTimeout);
    this.#qomoorResizeTimeout = setTimeout(() => {
      const batchSize = this.#qomoorBatchSize();
      if (batchSize === this.#qomoorLastBatchSize) return;
      this.#resetQomoorProducts();
    }, 160);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
